import { Modal, ModalBody, ModalHeader, ROLE, SIZE } from 'baseui/modal'
import React, { useContext, useEffect } from 'react'
import { ButtonType, CustomButton, InfoTooltip, ToastTypes, launchToast } from '../../components'
import { useBasicFormSyncStore, useFormSaveStore, useModalStateStore } from '../../utils'
import { useLiveViewStateStore } from '../LiveView/LiveViewStore'
import { DeviceType } from '../DeviceConfig'
import { BasicConfig } from '../DeviceConfig/BasicConfig'
import {
    getLiveViewAccessToken,
    getLiveViewObject,
    getLiveViewUrl,
    useSetDeviceForceUpdate,
    useUpdateDeviceConfigMutation
} from '../DeviceConfig/utils'
import { AutoWhiteList } from '../StationView/AutoWhiteList'
import { StationConfig } from '../StationView/StationConfig'
import { StationDirections } from '../StationView/station_types'
import {
    useGetDeviceData,
    useGetDeviceUuids,
    useSaveStation,
    useSaveStationConfig
} from '../StationView/utils/station-hooks'
import { LotContext } from './LotContext'
import { DeviceTypeEnum } from '../../data/enums'
import styles from './LotEditModal.module.css'
export const LotEditModal = ({ refetch }: { refetch: () => void }) => {
    const lotContext = useContext(LotContext)
    const [isCleared, setIsCleared] = React.useState(false)
    const { closeModal, isModalOpen } = useModalStateStore()
    const initialDevice = lotContext.editingStation?.device_uuid
    const { setBaseURL, setIsPmView, setLiveViewObject, setStationName } = useLiveViewStateStore()

    const stationName = lotContext.editingStation?.station_name ?? ''
    const [selectedDevice, setSelectedDevice] = React.useState<DeviceType | null | undefined>(undefined)

    const devices = useGetDeviceUuids()
    const [direction, setDirection] = React.useState<StationDirections | undefined | null>(undefined)
    const syncedDeviceConfig = useBasicFormSyncStore((state) => state.syncedState)
    const setSyncState = useBasicFormSyncStore((state) => state.setSyncState)
    const { setUnsavedCount } = useBasicFormSyncStore((state) => ({
        setUnsavedCount: state.setUnsavedCount
    }))
    const [autoWhitelistData, setAutowhitelistData] = React.useState<{
        enabled?: boolean
        duration?: number | null
    }>({})
    const setFormChanges = useFormSaveStore((state) => state.setFormChanges)
    const hasChanges = useFormSaveStore((state) => state.hasChanges)

    useEffect(() => {
        setUnsavedCount(0)
        setFormChanges(false)
    }, [setUnsavedCount, setFormChanges])
    const closeAndCleanUp = () => {
        setUnsavedCount(0)
        setFormChanges(false)
        closeModal('LotEditModal')
        lotContext.setEditingStation(undefined)
        setIsCleared(false)
        setSelectedDevice(undefined)
        setDirection(undefined)
        setSyncState(undefined)
    }
    const { setDeviceForceUpdate, loadingForceUpdate } = useSetDeviceForceUpdate(() => {
        refetch()
        launchToast(ToastTypes.Success, 'Successfully force updated config!')
        closeAndCleanUp()
    })
    const { updateDeviceConfig, loading: loadingDeviceConfigSave } = useUpdateDeviceConfigMutation()

    const { data: deviceData, refetch: refetchDeviceData } = useGetDeviceData(selectedDevice?.uuid)
    const loadedDeviceData = deviceData?.device[0]
    const forceUpdateDisabled =
        !!loadedDeviceData?.config_force_update ||
        loadedDeviceData?.config_current_timestamp === loadedDeviceData?.config_target_timestamp
    useEffect(() => {
        //clear the synced state when the modal is opened,so previous diff is not applied
        setSyncState(undefined)
    }, [])
    useEffect(() => {
        //clear the synced state when the modal is opened,so previous diff is not applied
        setSyncState(undefined)
    }, [selectedDevice])

    //set base url for live view
    useEffect(() => {
        if (loadedDeviceData) {
            setBaseURL(getLiveViewUrl(loadedDeviceData) || 'FAILURE')
            setIsPmView(true)
            setLiveViewObject(getLiveViewObject(deviceData.device[0]))

            setStationName(stationName || null)
            localStorage.setItem('liveViewAccessToken', getLiveViewAccessToken(deviceData.device[0]) || 'FAILURE')
        }
    }, [loadedDeviceData])

    //save direction and device_uuid
    const { saveStation, loadingSaveStation } = useSaveStation()

    //save base config
    const { saveStationConfig, loadingSaveStationConfig } = useSaveStationConfig(undefined, undefined, false)

    const isSaving = loadingSaveStation || loadingSaveStationConfig || loadingDeviceConfigSave
    const returnValueIfNotUndefined = (value: any, alternative: any) => {
        if (value === undefined) {
            return alternative
        } else {
            return value
        }
    }
    useEffect(() => {
        if (!selectedDevice && !isCleared && initialDevice) {
            setSelectedDevice({
                __typename: 'device',
                uuid: initialDevice as string
            })
        }
    }, [selectedDevice, isCleared, initialDevice])
    useEffect(() => {
        if (isCleared) {
            setSelectedDevice(undefined)
        }
    }, [isCleared])

    const stationConfig = lotContext.editingStation?.config

    const handleSave = () => {
        if (lotContext.editingStation) {
            saveStation(
                direction ? (direction as string) : (lotContext?.editingStation?.device_direction as string),
                { __typename: 'station', station_name: stationName },
                lotContext.editingStation.lot_section_id,
                selectedDevice?.uuid || (isCleared ? null : initialDevice ? initialDevice : null),
                () => {
                    saveStationConfig(
                        returnValueIfNotUndefined(
                            autoWhitelistData.enabled,
                            Boolean(stationConfig?.autowhitelist?.enable ?? false)
                        ),
                        returnValueIfNotUndefined(
                            autoWhitelistData.duration,
                            stationConfig?.autowhitelist?.duration_days
                        ),
                        { __typename: 'station', station_name: stationName },
                        //@ts-ignore
                        lotContext?.editingStation,
                        () => {
                            if (!(syncedDeviceConfig && !isCleared)) {
                                //the device config is not going to be updated
                                refetch()
                            }
                        }
                    )
                    //save base config after station is saved, so that the timestamp is updated
                    if (syncedDeviceConfig && !isCleared && selectedDevice?.uuid) {
                        console.log('syncedDeviceConfig', syncedDeviceConfig)
                        refetchDeviceData()
                            .then(async (response) => {
                                try {
                                    await updateDeviceConfig({
                                        device_uuid: selectedDevice.uuid,
                                        diff_config: syncedDeviceConfig,
                                        timestamp: response.data?.device[0].config_target_timestamp ?? ''
                                    })
                                    refetch()
                                    refetchDeviceData()
                                    setUnsavedCount(0)
                                    setFormChanges(false)
                                    //closeAndCleanUp()
                                    launchToast(ToastTypes.Success, 'Successfully updated config!')
                                    setSyncState(undefined)
                                } catch (error) {
                                    launchToast(ToastTypes.Error, 'Error updating device config!')
                                    console.log(error)
                                }
                            })
                            .catch((error) => {
                                console.log('error', error)
                                launchToast(ToastTypes.Error, 'Error fetching device data!')
                            })
                    } else {
                        setUnsavedCount(0)
                        setFormChanges(false)

                        // closeAndCleanUp()
                    }
                },
                "Saved! Don't forget to force update to apply the changes! "
            )
        }
    }

    const forceUpdate = () => {
        if (!selectedDevice?.uuid) return
        setDeviceForceUpdate({ device_uuid: selectedDevice.uuid }).catch(() => {})
    }
    return (
        <div>
            <Modal
                onClose={() => {
                    if (hasChanges) {
                        if (window.confirm('Are you sure you want to close without saving?')) {
                            closeAndCleanUp()
                        }
                    } else {
                        closeAndCleanUp()
                    }
                }}
                closeable
                isOpen={isModalOpen('LotEditModal')}
                animate
                autoFocus
                size={SIZE.full}
                role={ROLE.dialog}
            >
                <ModalHeader>Station {stationName}</ModalHeader>
                <ModalBody>
                    <div className={styles.modalContent}>
                        <StationConfig
                            //hardcoded since LotEditModal is only working for scanners
                            stationType={DeviceTypeEnum.SCANNER}
                            onDeviceChange={(device) => {
                                if (!device) {
                                    setSyncState(undefined)
                                    setIsCleared(true)
                                    return
                                }
                                setSelectedDevice(device)
                                setIsCleared(false)
                            }}
                            standalone={true}
                            devices={devices.data?.device || []}
                            stationDeviceUUID={initialDevice}
                            initialDirection={lotContext?.editingStation?.device_direction}
                            onDirectionChange={(direction: StationDirections) => {
                                setDirection(direction)
                            }}
                        />
                        <AutoWhiteList
                            standalone={true}
                            loading={false}
                            config={lotContext.editingStation?.config}
                            saveAutoWhitelistConfig={function (): void {}}
                            storeAutoWhiteListDuration={function (duration: number | null): void {
                                setAutowhitelistData({ ...autoWhitelistData, duration })
                            }}
                            storeAutoWhiteListEnabled={function (enabled: boolean): void {
                                setAutowhitelistData({ ...autoWhitelistData, enabled })
                            }}
                        />

                        {selectedDevice && loadedDeviceData && !isCleared && (
                            <BasicConfig
                                deviceType={DeviceTypeEnum.SCANNER}
                                standalone={true}
                                deviceData={loadedDeviceData}
                                selectedDevice={selectedDevice}
                                editDiffConfig={() => {}}
                                setDeviceForceUpdateAction={() => {}}
                                loadingSaveBasicConfig={false}
                            />
                        )}
                    </div>
                    <div className={styles.footer}>
                        <div className={styles.saveButton}>
                            <CustomButton
                                disabled={!hasChanges || isSaving}
                                onClick={handleSave}
                                isLoading={isSaving}
                                type={ButtonType.SAVE}
                            >
                                Save
                            </CustomButton>
                        </div>
                        <InfoTooltip
                            content={selectedDevice?.uuid && forceUpdateDisabled ? 'No changes to force update' : null}
                        >
                            <CustomButton
                                disabled={!selectedDevice?.uuid || forceUpdateDisabled || loadingForceUpdate}
                                type={ButtonType.DELETE}
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to force update the device?')) {
                                        forceUpdate()
                                    }
                                }}
                                isLoading={loadingForceUpdate}
                            >
                                Force update
                            </CustomButton>
                        </InfoTooltip>
                    </div>
                </ModalBody>
            </Modal>
        </div>
    )
}
